import Styles from "./CaptchaField.module.scss";
import { TextField, IconButton } from "@mui/material";
import { Sync } from "@mui/icons-material";

/**
 * A reusable captcha input component with captcha image, refresh button, and text field.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {{ id: string, image: string, input: string }} props.captcha - Captcha object containing id (UUID4), image (base64 or URL) and current input value.
 * @param {() => void} props.onRefreshCaptcha - Callback triggered when the refresh button is clicked to request a new captcha.
 * @param {(value: string) => void} props.onCaptchaChange - Callback triggered when the user types in the captcha input field.
 * @param {() => void} [props.onBlur] - Optional callback triggered when the captcha input loses focus.
 * @param {boolean} [props.error=false] - Whether the captcha input is in an error state.
 * @param {string} [props.helperText=""] - Helper or error message displayed under the captcha input.
 *
 * @example
 * <CaptchaField
 *   captcha={{ id: "050cb32a-6ea4-491c-be47-82e6234f20c8", image: "data:image/png;base64,...", input: "" }}
 *   onRefreshCaptcha={handleRefresh}
 *   onCaptchaChange={(val) => setCaptcha((prev) => ({ ...prev, input: val }))}
 *   onBlur={() => values.handleBlur("captcha")}
 *   error={values.touched.captcha && !!values.errors.captcha}
 *   helperText={values.touched.captcha && values.errors.captcha}
 * />
 */
const CaptchaField = ({
    captcha,
    onRefreshCaptcha,
    onCaptchaChange,
    onBlur,
    error = false,
    helperText = "",
}) => {
    return (
        <div id="captchaContainer" className={Styles.captchaContainer}>
            {/* Captcha image and refresh button */}
            <div id="captchaImageContainer" className={Styles.captchaImageContainer}>
                <img
                    id="captchaImage"
                    src={captcha?.image}
                    alt="captcha"
                    style={{ border: "1px solid #ccc", borderRadius: 4 }}
                />
                <div id="refreshButtonContainer" className={Styles.refreshButtonContainer}>
                    <IconButton id="refreshButton" onClick={onRefreshCaptcha}>
                        <Sync fontSize="large" />
                    </IconButton>
                </div>
            </div>

            {/* Captcha input field */}
            <div id="captchaInputFieldContainer" className={Styles.captchaInputFieldContainer}>
                <TextField
                    id="captchaInputField"
                    label="Enter the result"
                    variant="outlined"
                    value={captcha?.input || ""}
                    onChange={(e) => onCaptchaChange(e.target.value)}
                    onBlur={onBlur}
                    error={error}
                    helperText={helperText}
                    fullWidth
                />
            </div>
        </div>
    );
};

export default CaptchaField;

